<template>
  <component-wrapper class="download-center-fi">
    <grid-container-fi>
      <div class="fi-col-start-1 fi-col-end-13 lg:fi-col-start-2 lg:fi-col-end-12">
        <h1
          v-if="fields.headline?.value && !isEditMode"
          class="download-center-fi__headline"
        >
          {{ fields.headline?.value }}
        </h1>
        <!-- eslint-disable vue/no-v-html -->
        <div
          v-if="fields.headline && isEditMode"
          class="download-center-fi__headline"
          v-html="fields.headline.editable"
        />
        <!-- eslint-enable vue/no-v-html -->
      </div>
    </grid-container-fi>

    <template v-if="(!baseFacetsLoading && !initialLoading) || isEditMode">
      <grid-container-fi
        v-if="!isEditMode"
        class="download-center-fi__facets"
      >
        <div class="fi-col-span-full lg:fi-col-start-2 lg:fi-col-end-12">
          <div class="fi-grid fi-grid-cols-12 fi-gap-small md:fi-gap-large">
            <div class="fi-col-span-12 sm:fi-col-span-6 md:fi-col-span-4 lg:fi-col-span-3">
              <text-input-fi
                :value="query"
                :label="fields.searchLabel?.value"
                icon="search"
                large-from="sm"
                @change="searchChanged"
                @icon-clicked="searchChanged"
              />
            </div>
          </div>
        </div>
        <div class="fi-col-span-full lg:fi-col-start-2 lg:fi-col-end-12">
          <facets-menu-fi
            class="download-center-fi__facet-menu"
            :facets="documentFacets"
            :columns-max="4"
            @change="changeFacet"
            @clear-all-facets="clearAllFacets"
          >
            <template #facets="{ facet, open, change, close, toggleOpen, idx, registerDomElement }">
              <document-download-facet-fi
                :facet="facet"
                :open="open"
                :idx="idx"
                :register-dom-element="registerDomElement"
                @change="change"
                @toggle-open="toggleOpen(facet)"
                @close="close(facet)"
              />
            </template>
          </facets-menu-fi>
        </div>
      </grid-container-fi>

      <div class="download-center-fi__documents-wrapper">
        <loading-indicator v-if="queryLoading" />
        <grid-container-fi>
          <div class="fi-col-start-1 fi-col-end-13 lg:fi-col-start-2 lg:fi-col-end-12">
            <div
              v-if="(getDocumentListNg && documents) || isEditMode"
              class="download-center-fi__documents"
            >
              <div class="download-center-fi__documents__counter">
                {{
                  $dict.get('download-Center.Document-Downloads.Results-Counter', [
                    getDocumentListNg.resultCount,
                  ])
                }}
              </div>

              <template v-if="getDocumentListNg.resultCount > 0">
                <document-download-items-fi
                  :documents="documents"
                  theme="download-center"
                />

                <div class="download-center-fi__loading-more">
                  <infinite-loading
                    v-if="infiniteLoadingAvailable && !IS_SSR"
                    ref="infiniteLoading"
                    class="download-center-fi__spinner"
                    @infinite="fetchMore"
                  >
                    <template #spinner>
                      <div class="download-center-fi__spinner-tile">
                        <loading-indicator variant="spinnerLocal" />
                      </div>
                    </template>
                    <template #no-more><span /></template>
                    <template #no-results><span /></template>
                    <template #error><span /></template>
                  </infinite-loading>
                  <div
                    v-for="(item, idx) in placeholders"
                    :key="idx"
                    class="download-center-fi__spinner"
                  >
                    <div class="download-center-fi__spinner-tile" />
                  </div>
                </div>
              </template>
              <template v-else>
                <richtext-fi
                  class="download-center-fi__no-results"
                  :html-content="fields.noResults"
                />
              </template>
            </div>

            <div
              v-if="!isEditMode"
              class="download-center-fi__pagination"
            >
              <pagination-progress-bar
                v-if="getDocumentListNg.resultCount"
                :pagination-page="page"
                :pagination-total="getDocumentListNg.resultCount || 0"
                :progressbar-value="pagingPercentage"
                :disabled-state="Boolean(queryLoading)"
                :show-more-button="displayedItems < getDocumentListNg.resultCount"
                @loadmore="fetchMore"
              >
                <template v-if="page * quantity <= getDocumentListNg.resultCount">
                  <span>{{
                    $dict.get('download-Center.Pagination.Show-Sum', [
                      page * quantity,
                      getDocumentListNg.resultCount,
                    ])
                  }}</span>
                </template>

                <template v-else>
                  <span>{{
                    $dict.get('download-Center.Pagination.Show-Sum', [
                      getDocumentListNg.resultCount,
                      getDocumentListNg.resultCount,
                    ])
                  }}</span>
                </template>
              </pagination-progress-bar>
            </div>
          </div>
        </grid-container-fi>
      </div>
    </template>

    <div
      v-else
      class="download-center-fi__loading"
    >
      <loading-indicator
        variant="spinnerLocal"
        class="spinner-local--rebrush"
      />
    </div>
  </component-wrapper>
</template>

<script setup lang="ts">
import { useQuery } from '@urql/vue';
import { LoadingIndicator, PaginationProgressBar, RichtextFi, TextInputFi } from 'atoms';
import InfiniteLoadingBase from 'infinite-loading-vue3-ts';
import { computed, onServerPrefetch, ref, watch } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import DocumentDownloadFacetFi from '@/components/document-download/document-download-facet/DocumentDownloadFacetFi.vue';
import DocumentDownloadItemsFi from '@/components/document-download/DocumentDownloadItemsFi.vue';
import FacetsMenuFi from '@/components/facets/facets-menu-fi/FacetsMenuFi.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import mergeFacets from '@/components/search-fact-finder-fi/mergeFacets';
import { useFischer } from '@/composables/fischerPlugin';
import { useSalesChannelFilter } from '@/composables/salesChannelFilter';
import type { GetDocumentListNgQuery, GetDocumentListNgQueryVariables } from '@/generated/graphql';
import GETDOCUMENTLIST from '@/graphql/getDocumentListNg.gql';
import { useNotificationStore } from '@/store/pinia/notification';
import { useSearchStore } from '@/store/pinia/search';

// Cast to any to work around missing slot type declarations in infinite-loading-vue3-ts
const InfiniteLoading = InfiniteLoadingBase as any;

type DocumentListNgFacet = NonNullable<
  NonNullable<NonNullable<GetDocumentListNgQuery['getDocumentListNg']>['facets']>[number]
>;

// these should not be visible to the user
const technicalFacets = [
  'Type',
  'CategoryPathName',
  'Format',
  'ValidFrom',
  'ValidUntil',
  'FamilyPage',
];

defineOptions({ name: 'DownloadCenterFi' });

const props = withDefaults(
  defineProps<{
    fields?: object;
  }>(),
  {
    fields: () => ({}),
  },
);

const fischer = useFischer();
const searchStore = useSearchStore();
const { withSalesChannelFilter, documentSupplier } = useSalesChannelFilter();
const { openGeneralErrorNotification } = useNotificationStore();

const fields = computed(() => props.fields as any);

const accumulatedDocuments = ref<any[]>([]);
const getDocumentListNgData = ref<any>({
  resultStatus: null,
  resultCount: 0,
  currentPage: 0,
  nextLink: null,
  facets: [],
  documents: [],
});
const query = ref('');
const filters = ref<any[]>([]);
const getBaseFacetsData = ref<any[]>([]);
const quantity = ref(6);
const page = ref(1);
const fetchingMore = ref<false | (() => void)>(false);
const initialLoading = ref(true);
let haveParsedUrlParams = false;

const channel = computed(() => fields.value.channel?.value || fischer.factFinderChannel());
const language = computed(() => fields.value.language?.value || fischer.catalogLocale());

const queryTrimmed = computed(() => query.value.trim());

const graphQlVariables = computed(() => ({
  factFinderChannel: channel.value,
  baseSiteId: fischer.baseSiteId(),
  language: language.value,
  filters: withSalesChannelFilter(filters.value),
  quantity: quantity.value,
  query: encodeURIComponent(queryTrimmed.value) || '*',
  supplier: documentSupplier(),
  page: page.value,
}));

const pauseDocumentList = computed(() => fischer.app.isEditMode());

// Main document list query
const {
  data: documentData,
  fetching: queryLoading,
  error: documentListError,
  executeQuery: executeDocumentListQuery,
} = useQuery<GetDocumentListNgQuery, GetDocumentListNgQueryVariables>({
  query: GETDOCUMENTLIST,
  variables: graphQlVariables,
  pause: pauseDocumentList,
});

watch(documentListError, (e) => {
  if (e) {
    console.error('error getting document list', e);
    initialLoading.value = false;
    openGeneralErrorNotification();
  }
});

watch(
  documentData,
  (data) => {
    if (data?.getDocumentListNg) {
      if (page.value === 1) {
        accumulatedDocuments.value = data.getDocumentListNg.documents ?? [];
        getDocumentListNgData.value = data.getDocumentListNg;
      } else {
        accumulatedDocuments.value = [
          ...accumulatedDocuments.value,
          ...(data.getDocumentListNg.documents ?? []),
        ];
        getDocumentListNgData.value = {
          ...data.getDocumentListNg,
          documents: accumulatedDocuments.value,
        };
      }
    }
    initialLoading.value = false;
    if (fetchingMore.value) {
      const cb = fetchingMore.value;
      fetchingMore.value = false;
      setTimeout(cb, 1000);
    }
  },
  { immediate: true, flush: 'sync' },
);

onServerPrefetch(async () => {
  if (pauseDocumentList.value) return;
  await executeDocumentListQuery();
});

const baseFacetsVariables = computed(() => ({
  factFinderChannel: channel.value,
  baseSiteId: fischer.baseSiteId(),
  language: language.value,
  filters: withSalesChannelFilter([]),
  quantity: 1,
  query: '*',
  supplier: documentSupplier(),
  page: 1,
}));

const pauseBaseFacets = computed(() => fischer.app.isEditMode());

// Base facets query (no filters, for baseline)
const {
  data: baseFacetsData,
  fetching: baseFacetsLoading,
  error: baseFacetsError,
  executeQuery: executeBaseFacetsQuery,
} = useQuery<GetDocumentListNgQuery, GetDocumentListNgQueryVariables>({
  query: GETDOCUMENTLIST,
  variables: baseFacetsVariables,
  pause: pauseBaseFacets,
});

watch(baseFacetsError, (e) => {
  if (e) {
    console.error('could not fetch document facets', e);
    openGeneralErrorNotification();
  }
});

// immediate + flush:'sync' - see createSsrExchange. immediate: a restored SSR result is
// delivered during setup, so a lazy watcher never sees it. sync: Vue drops non-sync watchers
// during SSR entirely, so without it this state is never filled on the server.
// immediate: restored SSR results are delivered synchronously during setup, so a lazy watcher
// would never see them and the facets would stay empty after hydration.
watch(
  baseFacetsData,
  (data) => {
    if (data?.getDocumentListNg?.facets) {
      getBaseFacetsData.value = data.getDocumentListNg.facets?.filter(
        (facet: DocumentListNgFacet | null) => facet?.elements?.length,
      );
    }
  },
  { immediate: true, flush: 'sync' },
);

onServerPrefetch(async () => {
  if (pauseBaseFacets.value) return;
  await executeBaseFacetsQuery();
});

const getDocumentListNg = computed(() => getDocumentListNgData.value);

const infiniteLoadingAvailable = computed(
  () =>
    getDocumentListNg.value.resultCount &&
    displayedItems.value < getDocumentListNg.value.resultCount,
);

const placeholders = computed(() => {
  if (!infiniteLoadingAvailable.value) {
    return [];
  }
  return Array.from('x'.repeat(quantity.value - 1));
});

const displayedItems = computed(() =>
  Math.min(page.value * quantity.value, getDocumentListNg.value.resultCount),
);

const pagingPercentage = computed(
  () => (100 * displayedItems.value) / getDocumentListNg.value.resultCount,
);

const documents = computed(() => getDocumentListNg.value.documents);

const baseFacets = computed(() => [
  ...getBaseFacetsData.value,
  {
    associatedFieldName: fields.value.searchLabel?.value,
    type: 'search',
    name: '',
    noMenu: true,
    elements: [
      {
        name: '',
        selected: false,
        type: 'search',
      },
    ],
  },
]);

const documentFacets = computed(() => {
  let allGroups = JSON.parse(JSON.stringify(getDocumentListNg.value.facets)).map((facet: any) => ({
    ...facet,
    elements: facet.elements.map((element: any) => ({
      ...element,
      associatedFieldName: facet.associatedFieldName,
    })),
  }));

  allGroups = allGroups.filter((group: any) => !technicalFacets.includes(group.name));

  return mergeFacets(
    getDocumentListNg.value.facetDataUnselected || [],
    allGroups,
    baseFacets.value || [],
    filters.value,
  );
});

const filterUrlParamsNoQuery = computed(() => {
  const search: string[] = [];

  filters.value.forEach((filter) => {
    if (filter.selectedMinValue || filter.selectedMaxValue) {
      search.push(
        `filter${encodeURIComponent(filter.associatedFieldName)}=${encodeURIComponent(
          filter.selectedMinValue,
        )}-${encodeURIComponent(filter.selectedMaxValue)}`,
      );
    } else {
      search.push(
        `filter${encodeURIComponent(filter.associatedFieldName)}=${encodeURIComponent(
          filter.name,
        )}`,
      );
    }
  });

  if (search.length === 0) {
    return '';
  }

  return `?${search.join('&')}`;
});

const filterUrlParams = computed(() => {
  const q = queryTrimmed.value;
  if (!q) {
    return filterUrlParamsNoQuery.value;
  }
  const search = filterUrlParamsNoQuery.value ? `${filterUrlParamsNoQuery.value}&` : '?';
  return `${search}query=${encodeURIComponent(q)}`;
});

const searchChanged = (value: any) => {
  page.value = 1;
  query.value = (typeof value === 'string' ? value : '').trim();
  changeFacet();
};

const clearAllFacets = () => {
  filters.value = [];
  searchChanged('');
};

const changeFacet = (...changes: any[]) => {
  let newFilters = [...filters.value];

  changes.forEach(({ value, element: facet }) => {
    if (facet?.type !== 'search') {
      newFilters = newFilters.filter(
        (oldFacet) =>
          oldFacet.associatedFieldName !== facet.associatedFieldName &&
          oldFacet.text !== facet.text,
      );
      if (value) {
        newFilters.push({
          associatedFieldName: facet.associatedFieldName,
          selected: facet.selected,
          name: facet.text,
          type: facet.type,
        });
      }
    }
  });

  page.value = 1;

  filters.value = newFilters.sort((first, second) =>
    first.associatedFieldName.localeCompare(second.associatedFieldName),
  );

  if (!IS_SSR) {
    let state = filterUrlParams.value;

    const pageParam = fischer.query('page');
    if (pageParam) {
      state = state
        ? `${state}&page=${encodeURIComponent(pageParam)}`
        : `?page=${encodeURIComponent(pageParam)}`;
    }

    const customQuery: string[] = [];
    const queryObj = fischer.query() || {};
    const filterKeys = ['filter', 'query'];
    Object.entries(queryObj).forEach(([key, val]) => {
      if (!filterKeys.some((el) => key.startsWith(el))) {
        customQuery.push(`${key}=${encodeURIComponent(val as string)}`);
      }
    });

    if (customQuery.length) {
      if (filterUrlParams.value) {
        state = `${filterUrlParams.value}&${customQuery.join('&')}`;
      } else {
        state = `?${customQuery.join('&')}`;
      }
    }

    if (state !== String(window.location.search)) {
      window.history.replaceState('', '', state || '?');
    }
  }
};

const getPageParam = () => {
  const queryParam = fischer.query('page');
  return Number(queryParam) || 1;
};

const parseUrlParams = () => {
  if (haveParsedUrlParams) {
    return;
  }
  haveParsedUrlParams = true;

  query.value = fischer.query('query') || '';

  const pageParam = getPageParam();

  const technicalFacetsLowerCase = technicalFacets.map((name) => name.toLowerCase());
  const urlFilters: any[] = [];
  const search = fischer.query() || {};
  Object.keys(search).forEach((key) => {
    const match = /filter(.*)/.exec(key);
    if (!match) {
      return;
    }

    const [, name] = match;
    const nameLowerCase = name.toLowerCase();
    if (!name || technicalFacetsLowerCase.includes(nameLowerCase)) {
      return;
    }

    if (/[^~]~~[^~]/.test(name)) {
      const values = /^(\d+)-(\d+)/.exec(search[key] as string);
      if (values) {
        urlFilters.push({
          element: {
            associatedFieldName: name,
            selectedMinValue: values[1],
            selectedMaxValue: values[2],
            selected: true,
          },
          value: true,
        });
        return;
      }
    }
    urlFilters.push({
      element: {
        associatedFieldName: name,
        text: search[key],
        selected: true,
      },
      value: true,
    });
  });

  const urlQuery = fischer.query('query');
  if (urlQuery && urlQuery !== query.value) {
    searchStore.query = urlQuery;
    searchStore.queryTrimmed = urlQuery.trim();
  }

  changeFacet(...urlFilters);

  page.value = pageParam;
};

// Parse URL params on first use
parseUrlParams();

const fetchMore = async ({ complete, loaded }: any = {}) => {
  if (fetchingMore.value) {
    return;
  }
  if (page.value * quantity.value > getDocumentListNg.value.resultCount) {
    if (complete) {
      complete();
    }
    return;
  }
  fetchingMore.value = loaded || (() => null);
  page.value += 1;
  // urql auto-re-runs because graphQlVariables computed uses page.value
  // loaded() is called inside watch(documentData, ...) when data arrives
};
</script>

<style scoped src="./download-center-fi" />
